/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { initFacebookSDK } from "./facebookConfig";
import ModeSwitch from "./components/ModeSwitch";
import InputSection from "./components/InputSection";
import Loader from "./components/Loader";
import PostTableWrapper from "./components/PostTableWrapper";
import LoginButton from "./LoginButton";
import Navbar from "./Navbar";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsAndConditions from "./TermsAndConditions";
import Footer from "./Footer";

function App() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [postData, setPostData] = useState(null);
  const [inputUrl, setInputUrl] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [sortOption, setSortOption] = useState("date");
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedPageAccessToken, setSelectedPageAccessToken] = useState("");
  const [isPublicPage, setIsPublicPage] = useState(false);

  useEffect(() => {
    initFacebookSDK();
    const storedUser = localStorage.getItem("facebookUser");
    const storedToken = localStorage.getItem("facebookAccessToken");

    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedToken) {
      setAccessToken(storedToken);

      // โหลดเพจถ้าอยู่ใน Admin Mode
      if (!isPublicPage) fetchPages(storedToken);
    }
  }, [isPublicPage]);

  const fetchPages = (token) => {
    setLoading(true);
    window.FB.api("/me/accounts", { access_token: token }, (response) => {
      setLoading(false);
      if (response && response.data) {
        setPages(response.data);
        if (response.data.length > 0) {
          setSelectedPageId(response.data[0].id);
          setSelectedPageAccessToken(response.data[0].access_token);
          setInputUrl(`https://www.facebook.com/${response.data[0].id}`);
        } else {
          setPages([]);
          setSelectedPageId("");
          setSelectedPageAccessToken("");
          alert("No admin pages available.");
        }
      } else {
        alert("Unable to fetch pages. Please check your permissions.");
      }
    });
  };

  const handlePageChange = (e) => {
    const pageId = e.target.value;
    setSelectedPageId(pageId);
    const selectedPage = pages.find((page) => page.id === pageId);
    if (selectedPage) {
      setSelectedPageAccessToken(selectedPage.access_token);
      setInputUrl(`https://www.facebook.com/${pageId}`);
    }
  };

  const extractPostOrPageId = (url) => {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split("/").filter(Boolean);
      const pageId = urlObj.searchParams.get("id");
      if (pageId) return pageId;
      return pathSegments[0];
    } catch (error) {
      console.error("Invalid URL:", error);
      return null;
    }
  };

  const fetchPostData = () => {
    if (isPublicPage) {
      const id = extractPostOrPageId(inputUrl);
      if (id) {
        callGraphAPI(id, accessToken);
      } else {
        alert("Invalid URL. Please enter a valid public page link.");
      }
    } else if (selectedPageId && selectedPageAccessToken) {
      callGraphAPI(selectedPageId, selectedPageAccessToken);
    }
  };

  const callGraphAPI = (id, token) => {
    setLoading(true);
    window.FB.api(
      `/${id}/posts`,
      {
        access_token: token,
        fields: "message,created_time,likes.summary(true),comments.summary(true),shares",
      },
      (response) => {
        setLoading(false);
        if (response && response.error) {
          alert(
            `Error fetching data: ${response.error.message || "Permission denied. Please check your token or Facebook API permissions."}`
          );
          console.error("Graph API Error:", response.error);
          return;
        }
        if (response && response.data) {
          setPostData(response);
        }
      }
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("facebookUser");
    localStorage.removeItem("facebookAccessToken");
    localStorage.removeItem("facebookPageAccessToken");

    setUser(null);
    setPostData(null);
    setAccessToken("");
    setPageAccessToken("");
    setPages([]);
    setSelectedPageId("");
    setSelectedPageAccessToken("");

    navigate("/");
  };

  const handleSwitchChange = () => {
    setIsPublicPage(!isPublicPage);
    setInputUrl("");
    setPostData(null);
  };

  const sortedPosts = () => {
    if (!postData || !postData.data) return [];
    return [...postData.data].sort((a, b) => {
      switch (sortOption) {
        case "likes":
          return (
            (b.likes?.summary?.total_count || 0) -
            (a.likes?.summary?.total_count || 0)
          );
        case "comments":
          return (
            (b.comments?.summary?.total_count || 0) -
            (a.comments?.summary?.total_count || 0)
          );
        case "shares":
          return (b.shares?.count || 0) - (a.shares?.count || 0);
        case "date":
        default:
          return new Date(b.created_time) - new Date(a.created_time);
      }
    });
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    const storedToken = localStorage.getItem("facebookAccessToken");
    if (storedToken) {
      setAccessToken(storedToken);
      if (!isPublicPage) fetchPages(storedToken);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow">
        <div
          className={
            user
              ? "w-full"
              : "flex items-center justify-center min-h-[96vh] bg-gradient-to-r from-blue-500 to-purple-600"
          }
        >
          {user && <Navbar user={user} onLogout={handleLogout} />}
          <Routes>
            <Route
              path="/"
              element={
                !user ? (
                  <LoginButton onLogin={handleLoginSuccess} />
                ) : (
                  <div className="w-full min-h-max bg-white">
                    <div className="mt-4 p-4 container mx-auto">
                      <ModeSwitch
                        isPublicPage={isPublicPage}
                        handleSwitchChange={handleSwitchChange}
                      />
                      <InputSection
                        isPublicPage={isPublicPage}
                        inputUrl={inputUrl}
                        setInputUrl={setInputUrl}
                        pages={pages}
                        selectedPageId={selectedPageId}
                        handlePageChange={handlePageChange}
                      />
                      <button
                        onClick={fetchPostData}
                        className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded mt-2"
                      >
                        Fetch Data
                      </button>
                    </div>

                    {/* Loader or PostTableWrapper */}
                    {loading ? (
                      <div className="flex justify-center mt-4">
                        <Loader loading={loading} />
                      </div>
                    ) : (
                      postData && (
                        <PostTableWrapper
                          postData={postData}
                          sortedPosts={sortedPosts}
                          sortOption={sortOption}
                          handleSortChange={setSortOption}
                          getButtonClass={(option) =>
                            option === sortOption
                              ? "bg-black text-white px-3 py-1 rounded mr-2"
                              : "bg-gray-200 text-black px-3 py-1 rounded mr-2"
                          }
                        />
                      )
                    )}
                  </div>
                )
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
          </Routes>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default App;
